import { createFileRoute } from '@tanstack/react-router'
import { useCategoryGuide, categoryGuideQueryOptions } from '~/features/guides/hooks/use-category-guide'
import { GuideViewer } from '~/components/common/GuideViewer'
import { Breadcrumb } from '~/components/common/Breadcrumb'
import { seo } from '~/utils/seo'
import { extractGuideTitle } from '~/utils/guide-title'
import { EDGE_CACHE_HEADERS } from '~/utils/cache'
import type { QueryClient } from '@tanstack/react-query'
import { Spinner } from '@heroui/react'
import { defaultLocale, localeToLanguageCode } from '~/lib/i18n/shared'
import { useTranslations } from 'use-intl'

function getFallbackSeoText(locale: string) {
  switch (locale) {
    case 'zh-CN': return { title: '美食指南 | TabeTabi', description: '探索日本地道美食指南。' }
    case 'zh-TW': return { title: '美食指南 | TabeTabi', description: '探索日本地道美食指南。' }
    case 'ja': return { title: 'グルメガイド | TabeTabi', description: '日本の本格的なグルメガイドを探索しましょう。' }
    case 'ko': return { title: '미식 가이드 | TabeTabi', description: '일본의 정통 미식 가이드를 탐색해보세요.' }
    default: return { title: 'Food Guide | TabeTabi', description: 'Explore authentic Japanese food guides.' }
  }
}

export const Route = createFileRoute('/{-$locale}/_public/categories/$categoryId')({
  loader: async ({ context, params }) => {
    const queryClient = context.queryClient as QueryClient
    // Extract lang code, falling back to defaultLocale
    const rawLocale = (params as any).locale || defaultLocale
    const lang = localeToLanguageCode(rawLocale)
    
    try {
      const guide = await queryClient.ensureQueryData(categoryGuideQueryOptions(params.categoryId, lang))
      return { guide, lang }
    } catch (e) {
      return { guide: null, lang }
    }
  },
  head: ({ loaderData, params }) => {
    const locale = (params as any).locale || defaultLocale
    const fallback = getFallbackSeoText(locale)
    
    const guide = loaderData?.guide
    const title = guide?.contentMd
      ? extractGuideTitle(guide.contentMd, fallback.title)
      : fallback.title

    const { meta, links } = seo({
      title,
      description: fallback.description,
      pathname: `/categories/${params.categoryId}`,
      locale,
    })

    return {
      meta,
      links,
    }
  },
  headers: () => EDGE_CACHE_HEADERS,
  component: CategoryGuidePage,
})

function CategoryGuidePage() {
  const { categoryId } = Route.useParams()
  const { lang } = Route.useLoaderData()
  const { data: guide, isLoading } = useCategoryGuide(categoryId, lang)
  const t = useTranslations('HomePage.guides')

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!guide || !guide.contentMd) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Guide Not Found</h1>
        <p className="mt-4 text-default-500">
          We could not find the guide for this category in your language.
        </p>
      </div>
    )
  }

  // Extract H1 title from markdown (first line starting with #)
  const lines = guide.contentMd.split('\n')
  const titleLine = lines.find((l: string) => l.startsWith('# '))
  const guideTitle = titleLine ? titleLine.replace(/^#\s+/, '') : 'Guide'
  // Remove H1 from content to avoid duplicate rendering
  const contentWithoutH1 = lines.filter((l: string) => l !== titleLine).join('\n')

  return (
    <main className="container mx-auto px-4 py-8">
      <div className="mx-auto w-full max-w-3xl">
        <Breadcrumb items={[
          { label: 'TabeTabi', href: '/' },
          { label: t('categoriesTitle'), href: '/categories' },
          { label: guideTitle },
        ]} />

        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-8 text-foreground">
          {guideTitle}
        </h1>
      </div>

      <GuideViewer contentMd={contentWithoutH1} />
    </main>
  )
}
